import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MessageCircle, Search, ShoppingBag } from 'lucide-react';
import './Products.css';

const CATS = ['All', 'Food & Spices', 'Textiles', 'Handicrafts'];

const PRODS = [
  { n:'Roasted Channa',                    c:'Food & Spices', o:'Maharashtra, India',     m:'National & International', img:'channa_roasted',   desc:'Crunchy, protein-rich roasted chickpeas — a classic healthy Indian snack enjoyed across the country and exported globally.',           tag:'Best Seller' },
  { n:'Turmeric Channa',                   c:'Food & Spices', o:'Maharashtra, India',     m:'National & International', img:'channa_turmeric',  desc:'Roasted channa seasoned with pure turmeric — nutritious, flavourful and naturally golden. Rich in antioxidants.',                    tag:'Popular'     },
  { n:'Indian Raw Spices',                 c:'Food & Spices', o:'Pan India',              m:'National & International', img:'spices_raw',        desc:"Authentic whole and ground spices sourced directly from India's finest spice-growing regions. Pure, aromatic and fresh.",              tag:'Authentic'   },
  { n:'Kolhapuri Onion Garlic Chilli Powder', c:'Food & Spices', o:'Kolhapur, Maharashtra', m:'National & International', img:'kolhapuri_masala', desc:'The iconic fiery Kolhapuri masala — bold onion, garlic and chilli blend, a signature of Maharashtrian cuisine.',                tag:'Signature'   },
  { n:'Nagari Black Masala',               c:'Food & Spices', o:'Maharashtra, India',     m:'National & International', img:'nagari_masala',    desc:'Traditional Nagari black masala — a unique regional spice blend with deep, complex flavour passed down generations.',               tag:'Traditional' },
  { n:'Organic Jaggery',                   c:'Food & Spices', o:'Maharashtra, India',     m:'National & International', img:'jaggery',          desc:'Pure, chemical-free jaggery from organic sugarcane. Natural sweetener loved across India and exported worldwide.',                   tag:'Organic'     },
  { n:'Atta — All Types',                  c:'Food & Spices', o:'Pan India',              m:'National',                 img:'atta',             desc:'Premium quality wheat flour in all varieties — whole wheat, multigrain and more. Fresh-milled and highly nutritious.',                  tag:'Fresh'       },
  { n:'Cotton Fabric',                     c:'Textiles',      o:'Gujarat, Maharashtra',   m:'National & International', img:'cotton_fabric',    desc:'Premium Indian cotton fabric — soft, breathable and available in wide range of weaves, colours and finishes.',                        tag:'Premium'     },
  { n:'Cotton Yarn',                       c:'Textiles',      o:'Gujarat, India',         m:'National & International', img:'cotton_yarn',      desc:'High-quality cotton yarn for weaving, knitting and textile manufacturing — consistent quality and strong tensile strength.',         tag:'Quality'     },
  { n:'Silk Saree',                        c:'Textiles',      o:'Varanasi / Karnataka',   m:'National & International', img:'silk_saree',       desc:"Exquisite handcrafted silk sarees from India's finest weaving regions — elegance, tradition and artistry in every thread.",           tag:'Handcrafted' },
  { n:'Paithani Saree',                           c:'Textiles',      o:'Maharashtra, India',       m:'National & International', img:'pathani',          desc:'The iconic Paithani saree from Paithan, Maharashtra — handwoven with pure silk and zari borders, a timeless symbol of Maharashtrian heritage.',              tag:'Traditional' },
  { n:'Resin Art — All Types',             c:'Handicrafts',   o:'Pune, Maharashtra',      m:'National & International', img:'resin_art',        desc:'Stunning handcrafted resin art in all forms — trays, coasters, wall art, decorative pieces and fully custom creations.',             tag:'Handmade'    },
];

/* ── Smart image component: tries jpg → jpeg → png → webp → fallback ── */
const EXTS = ['jpg', 'jpeg', 'png', 'webp'];

function ProductImage({ name, alt, className }) {
  const base = process.env.PUBLIC_URL + '/products/' + name;
  const [idx, setIdx] = useState(0);
  const [failed, setFailed] = useState(false);

  if (failed) {
    // All extensions failed — show nice placeholder
    return (
      <div className={className + ' prod-img-placeholder'}>
        <span className="prod-img-placeholder__emoji">🖼️</span>
        <span className="prod-img-placeholder__text">{alt}</span>
      </div>
    );
  }

  return (
    <img
      src={`${base}.${EXTS[idx]}`}
      alt={alt}
      className={className}
      loading="lazy"
      onError={() => {
        if (idx < EXTS.length - 1) {
          setIdx(idx + 1); // try next extension
        } else {
          setFailed(true); // all failed
        }
      }}
    />
  );
}

export default function Products() {
  const [cat, setCat] = useState('All');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);

  const list = PRODS.filter(p =>
    (cat === 'All' || p.c === cat) &&
    (!q || p.n.toLowerCase().includes(q.toLowerCase()) || p.c.toLowerCase().includes(q.toLowerCase()))
  );

  return (
    <div className="page-enter" style={{ paddingTop: 80 }}>
      <section className="page-hero">
        <div className="page-hero__stripe"/><div className="page-hero__dots"/>
        <div className="container page-hero__inner">
          <div className="tag" style={{ marginBottom: 16 }}>Our Products</div>
          <h1 className="page-hero__title">Products We <em>Trade</em></h1>
          <p className="page-hero__desc">
            Authentic food products, quality textiles and unique handcrafted goods — sourced from India's finest producers and delivered with care.
          </p>
        </div>
      </section>

      {/* Filter bar */}
      <div className="pf-bar">
        <div className="container">
          <div className="pf-row">
            <div className="pf-search">
              <Search size={14} color="var(--gray-4)"/>
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search products..."/>
            </div>
            <span className="pf-count">{list.length} products</span>
          </div>
          <div className="pf-cats">
            {CATS.map(c => (
              <button key={c} className={`pf-cat ${cat===c?'pf-cat--on':''}`} onClick={()=>setCat(c)}>{c}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Products grid */}
      <section className="prods-sec">
        <div className="container">
          <div className="prods-grid">
            {list.map((p, i) => (
              <div key={i} className="prod-card" onClick={() => setSelected(p)}>
                <div className="prod-card__img-wrap">
                  <ProductImage
                    name={p.img}
                    alt={p.n}
                    className="prod-card__img"
                  />
                  <div className="prod-card__overlay"><span>View Details</span></div>
                </div>
                <div className="prod-card__body">
                  <div className="prod-c">{p.c}</div>
                  <div className="prod-n">{p.n}</div>
                  <div className="prod-d"><strong>Origin:</strong> {p.o}</div>
                  <div className="prod-d"><strong>Markets:</strong> {p.m}</div>
                  <div className="prod-actions">
                    <a href="https://wa.me/919226318469" className="prod-wa" target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()}>
                      <MessageCircle size={12}/> WhatsApp
                    </a>
                    <Link to="/contact" className="prod-lnk" onClick={e=>e.stopPropagation()}>
                      Get Quote <ArrowRight size={11}/>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {list.length===0 && (
            <div className="prods-empty">
              <p>No products found. <button onClick={()=>{setQ('');setCat('All');}}>Clear filters</button></p>
            </div>
          )}
        </div>
      </section>

      {/* Product Detail Modal */}
      {selected && (
        <div className="prod-modal" onClick={()=>setSelected(null)}>
          <div className="prod-modal__box" onClick={e=>e.stopPropagation()}>
            <button className="prod-modal__close" onClick={()=>setSelected(null)}>✕</button>
            <div className="prod-modal__layout">
              <div className="prod-modal__img-wrap">
                <ProductImage
                  name={selected.img}
                  alt={selected.n}
                  className="prod-modal__img"
                />
              </div>
              <div className="prod-modal__info">
                <div className="prod-modal__cat">{selected.c}</div>
                <h2 className="prod-modal__name">{selected.n}</h2>
                <p className="prod-modal__desc">{selected.desc}</p>
                <div className="prod-modal__meta">
                  <div className="prod-modal__row"><strong>Origin:</strong> {selected.o}</div>
                  <div className="prod-modal__row"><strong>Markets:</strong> {selected.m}</div>
                  <div className="prod-modal__row"><strong>Supplier:</strong> Yashraj Traders India</div>
                  <div className="prod-modal__row"><strong>GSTIN:</strong> 27CBFPB6539R1Z2</div>
                </div>
                <div className="prod-modal__actions">
                  <a href="https://wa.me/919226318469" className="btn btn-fire" target="_blank" rel="noreferrer">
                    <MessageCircle size={15}/> WhatsApp Enquiry
                  </a>
                  <Link to="/contact" className="btn btn-ink" onClick={()=>setSelected(null)}>
                    <ShoppingBag size={15}/> Get Quote
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Custom sourcing */}
      <section style={{padding:'72px 0',background:'var(--white)'}}>
        <div className="container" style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:40,flexWrap:'wrap'}}>
          <div>
            <div className="tag" style={{marginBottom:14}}>Custom Orders</div>
            <h2 className="display" style={{marginBottom:12}}>Need Something <em>Specific?</em></h2>
            <p className="body-md" style={{maxWidth:480}}>We handle bulk orders, custom packaging and special requirements. Get in touch with Rhutuja directly.</p>
          </div>
          <div style={{display:'flex',gap:12,flexWrap:'wrap',flexShrink:0}}>
            <Link to="/contact" className="btn btn-fire">Send Enquiry <ArrowRight size={15}/></Link>
            <a href="https://wa.me/919226318469" className="btn btn-ink" target="_blank" rel="noreferrer">
              <MessageCircle size={15}/> WhatsApp Now
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
